import React, { useEffect, useRef, useState } from 'react';
import { OverflowMenu } from '../ui/OverflowMenu';
import { HiTrash, HiChevronUp, HiChevronDown, HiBars3, HiPrinter } from 'react-icons/hi2';
import { computeDisplayLabels } from '../../utils/questionnairePages';
import { getQuestionRowMeta } from '../../utils/questionOrdering';
import { normalizeCustomPageType } from '../../utils/questionnaireEditorUtils';
import { INTRO_PAGE_ID, DEFAULT_QUESTIONS_PAGE_ID, PAGE_TYPE_BASIS, PAGE_TYPE_FACH } from '../../utils/questionnaireEditorConstants';

/**
 * Shared shape for the rail's page cards.
 *
 * The rail is a narrow column, so a card is first a navigation target and only
 * then an editing surface: one line of title, one line of meta, and controls
 * that stay out of the way until the row is hovered or selected.
 */
function railCardClasses(isSelected, { isDragged, isDragOver } = {}) {
  return [
    // pl-5 on every card, selected or not, so the accent bar has its lane and
    // nothing shifts sideways when the selection moves.
    'group relative overflow-hidden rounded-xl border py-3 pl-5 pr-3 cursor-pointer transition-all duration-150',
    isSelected
      ? 'border-green-500 bg-green-50 shadow-sm dark:bg-green-900/25 dark:border-green-500'
      : 'border-transparent bg-white/70 hover:bg-white hover:border-slate-200 dark:bg-slate-800/50 dark:hover:bg-slate-800 dark:hover:border-slate-700',
    isDragged ? 'opacity-50' : '',
    isDragOver ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-white dark:ring-offset-slate-800' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Full-height accent on the open page.
 *
 * Deliberately loud: in a list of similar rows a tint alone is easy to miss,
 * and missing which page is open is the one mistake that costs real work.
 */
function SelectionBar() {
  return <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-green-500" />;
}

function PageNumber({ value, isSelected }) {
  return (
    <span
      className={`shrink-0 grid h-7 w-7 place-items-center rounded-lg text-xs font-bold tabular-nums ${
        isSelected
          ? 'bg-green-500 text-white'
          : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:group-hover:bg-slate-600'
      }`}
    >
      {value}
    </span>
  );
}

/**
 * The meta line doubles as the question-list toggle.
 *
 * Reordering questions across the whole questionnaire is a main job here, so
 * the lists have to be one visible click away — not buried in a menu.
 */
function MetaToggle({ children, isExpanded, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={isExpanded ? 'Fragenliste ausblenden' : 'Fragenliste anzeigen'}
      className="flex w-full items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
    >
      <span className="truncate">{children}</span>
      <HiChevronDown
        className={`w-3.5 h-3.5 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

/** Drag handle plus reorder arrows. Fades in on hover, stays for the open page. */
function RailControls({ isSelected, onDragStart, onDragEnd, onUp, onDown, disableUp, disableDown, menuItems }) {
  const iconButton =
    'p-1 rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors';

  return (
    <div
      className={`flex shrink-0 items-center transition-opacity ${
        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={(e) => e.stopPropagation()}
        className={`${iconButton} cursor-grab active:cursor-grabbing`}
        title="Seite ziehen"
      >
        <HiBars3 className="w-4 h-4" />
      </div>
      <button type="button" onClick={onUp} disabled={disableUp} className={iconButton} title="Nach oben">
        <HiChevronUp className="w-4 h-4" />
      </button>
      <button type="button" onClick={onDown} disabled={disableDown} className={iconButton} title="Nach unten">
        <HiChevronDown className="w-4 h-4" />
      </button>
      <OverflowMenu items={menuItems} label="Seiten-Aktionen" />
    </div>
  );
}

export function PagesPanel({
  questionPages,
  addQuestionPage,
  setSelectedPageId,
  selectedPageId,
  isIntroPageSelected,
  isDefaultQuestionsPageSelected,
  orderedEditorQuestionPages,
  handlePageCardSelect,
  handlePageCardDragOver,
  handlePageCardDragLeave,
  handlePageCardDrop,
  handlePageCardDragStart,
  handlePageCardDragEnd,
  draggedPageCardId,
  dragOverPageCardId,
  moveDefaultQuestionsPage,
  moveQuestionPage,
  removeQuestionPage,
  renameQuestionPage,
  updateQuestionPageDescription,
  updateQuestionPageType,
  defaultSectionTitle,
  setDefaultSectionTitle,
  defaultSectionDescription,
  setDefaultSectionDescription,
  defaultQuestions,
  introWelcomeText,
  introOverviewText,
  introAfterSectionsText,
  introTimerText,
  introStartButtonText,
  reviewSectionTitle,
  totalFormalQuestions,
  questions,
  onReorderPageQuestions,
  onReorderDefaultQuestions,
  onMovePageQuestion,
  onMoveDefaultQuestion,
  onJumpToQuestion,
  onExport,
  onImport,
}) {
  const stripHtml = (value = '') => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const escapeHtml = (value = '') => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  const toHtmlParagraph = (value = '') => escapeHtml(stripHtml(value)).replace(/\n/g, '<br />');
  const [expandedQuestionLists, setExpandedQuestionLists] = useState({});
  const [draggedQuestionIndexByPage, setDraggedQuestionIndexByPage] = useState({});
  const [dragOverQuestionIndexByPage, setDragOverQuestionIndexByPage] = useState({});

  /**
   * "Show everything" is its own mode, not the sum of every list being open.
   * Kept apart so switching pages cannot quietly cancel it.
   */
  const [showAllQuestionLists, setShowAllQuestionLists] = useState(false);

  /** Every page that owns a question list, in rail order. */
  const questionListKeys = [DEFAULT_QUESTIONS_PAGE_ID, ...questionPages.map((page) => page.id)];

  const isQuestionListExpanded = (pageKey) =>
    showAllQuestionLists || Boolean(expandedQuestionLists[pageKey]);

  /** The open page's list key, or null for the intro page, which owns none. */
  const selectedListKey = isIntroPageSelected
    ? null
    : isDefaultQuestionsPageSelected
      ? DEFAULT_QUESTIONS_PAGE_ID
      : selectedPageId;

  const toggleQuestionList = (pageKey) => {
    if (showAllQuestionLists) {
      // Closing one row while everything is open: leave the mode, keep the
      // rest open, and honour the click.
      setShowAllQuestionLists(false);
      setExpandedQuestionLists(
        Object.fromEntries(questionListKeys.map((key) => [key, key !== pageKey]))
      );
      return;
    }
    setExpandedQuestionLists((prev) => ({ ...prev, [pageKey]: !prev[pageKey] }));
  };

  /** One switch for the whole questionnaire — reordering questions is a job you
      do across pages, not page by page. */
  const toggleAllQuestionLists = () => {
    if (showAllQuestionLists) {
      setShowAllQuestionLists(false);
      setExpandedQuestionLists(selectedListKey ? { [selectedListKey]: true } : {});
      return;
    }
    setShowAllQuestionLists(true);
  };

  // Opening a page opens its questions and closes the page you left, so the
  // rail always shows exactly the one you are working on. Read through a ref:
  // this reacts to the selection changing, never to the mode being switched.
  const showAllRef = useRef(showAllQuestionLists);
  showAllRef.current = showAllQuestionLists;

  useEffect(() => {
    if (showAllRef.current) return;
    // Only where the rail is a companion column. Stacked above the editor on a
    // phone, auto-expanding buries the questions under the navigation.
    const isCompanionColumn =
      typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches;
    setExpandedQuestionLists(selectedListKey && isCompanionColumn ? { [selectedListKey]: true } : {});
  }, [selectedListKey]);

  const handleQuestionDragStart = (e, pageKey, index) => {
    e.stopPropagation();
    setDraggedQuestionIndexByPage((prev) => ({ ...prev, [pageKey]: index }));
  };

  const handleQuestionDragOver = (e, pageKey, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverQuestionIndexByPage((prev) => ({ ...prev, [pageKey]: index }));
  };

  const handleQuestionDragEnd = (pageKey) => {
    setDraggedQuestionIndexByPage((prev) => ({ ...prev, [pageKey]: undefined }));
    setDragOverQuestionIndexByPage((prev) => ({ ...prev, [pageKey]: undefined }));
  };

  const handleQuestionDrop = (e, pageKey, onReorder) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = draggedQuestionIndexByPage[pageKey];
    const toIndex = dragOverQuestionIndexByPage[pageKey];
    if (typeof fromIndex === 'number' && typeof toIndex === 'number' && fromIndex !== toIndex && typeof onReorder === 'function') {
      onReorder(fromIndex, toIndex);
    }
    handleQuestionDragEnd(pageKey);
  };

  const renderQuestionOverviewList = ({ pageKey, pageQuestions, onReorder, onMove }) => {
    const questionList = Array.isArray(pageQuestions) ? pageQuestions : [];
    if (questionList.length === 0) {
      return (
        <div className="mt-3 rounded-md border border-dashed border-slate-300 dark:border-slate-600 p-3 text-xs text-slate-500 dark:text-slate-400">
          Keine Fragen auf dieser Seite.
        </div>
      );
    }

    const labelMap = computeDisplayLabels(questionList, 0);
    const rowMeta = getQuestionRowMeta(questionList);
    const draggedIndex = draggedQuestionIndexByPage[pageKey];
    const dragOverIndex = dragOverQuestionIndexByPage[pageKey];
    const moveButtonClass = 'p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 disabled:opacity-30 disabled:hover:bg-transparent transition-colors';

    return (
      <div className="mt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
        {questionList.map((question, index) => {
          const label = labelMap[question?.id] || String(index + 1);
          const title = stripHtml(question?.text || `Frage ${index + 1}`);
          const isDragged = draggedIndex === index;
          const isDragOver = dragOverIndex === index && draggedIndex !== index;
          const { canMoveUp = false, canMoveDown = false, isSubQuestion = false } = rowMeta.get(String(question?.id)) || {};
          const handleMove = (e, direction) => {
            e.stopPropagation();
            if (typeof onMove === 'function') onMove(index, direction);
          };
          return (
            <div
              key={question?.id ?? `${pageKey}-${index}`}
              draggable
              onDragStart={(e) => handleQuestionDragStart(e, pageKey, index)}
              onDragOver={(e) => handleQuestionDragOver(e, pageKey, index)}
              onDrop={(e) => handleQuestionDrop(e, pageKey, onReorder)}
              onDragEnd={() => handleQuestionDragEnd(pageKey)}
              onClick={() => {
                if (question?.id != null && typeof onJumpToQuestion === 'function') {
                  onJumpToQuestion(pageKey, question.id);
                }
              }}
              title="Zur Frage springen"
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm bg-white dark:bg-slate-900 cursor-grab active:cursor-grabbing hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all ${
                isSubQuestion ? 'ml-6' : ''
              } ${
                isDragged ? 'opacity-60' : ''
              } ${
                isDragOver ? 'border-green-400 ring-1 ring-green-300 dark:ring-green-700' : 'border-slate-100 dark:border-slate-700/60'
              }`}
            >
              <HiBars3 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0">{label}.</span>
              {/* flex-1 + min-w-0 lets the title claim the free space and truncate,
                  which keeps the move buttons pinned to the right edge of the row. */}
              <span className="flex-1 min-w-0 truncate text-slate-700 dark:text-slate-200">{title || 'Unbenannte Frage'}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => handleMove(e, 'up')}
                  onDragStart={(e) => e.stopPropagation()}
                  disabled={!canMoveUp}
                  className={moveButtonClass}
                  aria-label={`Frage ${label} nach oben verschieben`}
                  title="Frage nach oben verschieben"
                >
                  <HiChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => handleMove(e, 'down')}
                  onDragStart={(e) => e.stopPropagation()}
                  disabled={!canMoveDown}
                  className={moveButtonClass}
                  aria-label={`Frage ${label} nach unten verschieben`}
                  title="Frage nach unten verschieben"
                >
                  <HiChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOptionsList = (options = []) => {
    if (!Array.isArray(options) || options.length === 0) {
      return '<div class="muted">Keine Optionen hinterlegt.</div>';
    }
    return `<ul class="option-list">${options.map((opt) => (
      `<li><span class="choice-box"></span><span>${escapeHtml(stripHtml(opt || '') || 'Option')}</span></li>`
    )).join('')}</ul>`;
  };

  const renderQuestionBody = (question) => {
    const questionType = String(question?.type || 'text');
    const answerType = String(question?.answerType || '');
    const options = Array.isArray(question?.options) ? question.options : [];

    if (questionType === 'single-choice' || questionType === 'multiple-choice' || questionType === 'ranking') {
      return renderOptionsList(options);
    }

    if (questionType === 'matrix') {
      const cols = options.length > 0 ? options : ['', '', ''];
      const rows = (question?.matrixMode || 'predefined') === 'predefined'
        ? (Array.isArray(question?.rowLabels) && question.rowLabels.length > 0 ? question.rowLabels : ['Zeile 1', 'Zeile 2'])
        : ['Eintrag 1', 'Eintrag 2'];
      return `
        <table class="matrix-table">
          <thead>
            <tr>
              <th></th>
              ${cols.map((col) => `<th>${escapeHtml(stripHtml(col || '') || 'Option')}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td>${escapeHtml(stripHtml(row || '') || 'Eintrag')}</td>
                ${cols.map(() => '<td><span class="choice-box"></span></td>').join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }

    if (questionType === 'combobox') {
      const suggestions = Array.isArray(question?.suggestions) ? question.suggestions : [];
      const isPredefined = (question?.comboboxMode || 'predefined') === 'predefined';
      const predefinedEntries = Array.isArray(question?.predefinedEntries)
        ? question.predefinedEntries.filter((entry) => stripHtml(entry || '').trim())
        : [];
      return `
        ${isPredefined && predefinedEntries.length > 0 ? `
          <div class="field-label">${escapeHtml(question?.rowInputLabel || 'Einträge')}</div>
          <ul class="suggestion-list">${predefinedEntries.map((entry) => `<li>${escapeHtml(stripHtml(entry || ''))}: ____________________</li>`).join('')}</ul>
        ` : `<div class="line-input">${escapeHtml(question?.rowInputLabel || 'Eingabe')}: __________________________________</div>`}
        ${options.length > 0 ? `
          <div class="field-label">${escapeHtml(question?.columnLabel || 'Auswahl')}</div>
          ${renderOptionsList(options)}
        ` : ''}
        ${suggestions.length > 0 ? `
          <div class="field-label">Vorschläge</div>
          <ul class="suggestion-list">${suggestions.map((s) => `<li>${escapeHtml(stripHtml(s || ''))}</li>`).join('')}</ul>
        ` : ''}
      `;
    }

    if (questionType === 'image') {
      if (answerType === 'single-choice' || answerType === 'multiple-choice') {
        return renderOptionsList(options);
      }
      return '<div class="line-input">Antwort: ________________________________________________</div>';
    }

    if (questionType === 'text') {
      return '<div class="answer-box"></div>';
    }

    return '<div class="line-input">Antwort: ________________________________________________</div>';
  };

  const renderQuestionItems = (pageQuestions = []) => {
    const normalizedQuestions = Array.isArray(pageQuestions) ? pageQuestions : [];
    const labelMap = computeDisplayLabels(normalizedQuestions, 0);
    return normalizedQuestions.map((question, index) => {
      const questionText = escapeHtml(stripHtml(question?.text || '') || `Frage ${index + 1}`);
      const questionDescription = escapeHtml(stripHtml(question?.description || ''));
      const label = escapeHtml(labelMap[question?.id] || String(index + 1));
      return `
        <section class="question-card">
          <div class="q-title">${label}. ${questionText}</div>
          ${questionDescription ? `<div class="q-description">${questionDescription}</div>` : ''}
          <div class="q-body">${renderQuestionBody(question)}</div>
        </section>
      `;
    }).join('');
  };

  const openPrintableDocument = ({ documentTitle, sections = [] }) => {
    if (typeof window === 'undefined') return;
    const popup = window.open('', '_blank');
    if (!popup) return;
    try {
      popup.history.replaceState({}, '', `${window.location.pathname}#print`);
    } catch (error) {
      // ignore
    }

    const sectionsHtml = sections.map((section, index) => `
      <section class="print-section ${index > 0 ? 'page-break' : ''}">
        <h1>${escapeHtml(section.title || 'Unbenannte Seite')}</h1>
        <div class="subtitle">${escapeHtml(section.pageLabel || '')}</div>
        ${section.description ? `<p class="description">${escapeHtml(stripHtml(section.description))}</p>` : ''}
        ${section.customContentHtml || ''}
        ${(Array.isArray(section.pageQuestions) && section.pageQuestions.length > 0)
          ? renderQuestionItems(section.pageQuestions)
          : (!section.customContentHtml && section.showEmptyQuestionsMessage !== false ? '<p>Keine Fragen auf dieser Seite vorhanden.</p>' : '')}
      </section>
    `).join('');

    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(documentTitle || 'Fragebogen')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 32px; color: #0f172a; }
            h1 { margin: 0 0 4px 0; font-size: 22px; }
            .subtitle { color: #475569; margin-bottom: 16px; }
            .description { margin: 0 0 20px 0; color: #334155; }
            .print-section { margin-bottom: 10px; }
            .page-break { page-break-before: always; }
            .intro-paragraph { margin: 0 0 10px 0; color: #334155; line-height: 1.45; }
            .section-list-title { margin: 14px 0 8px 0; font-size: 14px; font-weight: 700; color: #0f172a; }
            .section-list { margin: 0 0 14px 18px; padding: 0; color: #334155; }
            .section-list li { margin-bottom: 4px; }
            .question-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin: 0 0 12px 0; page-break-inside: avoid; }
            .q-title { font-weight: 700; margin-bottom: 6px; }
            .q-description { color: #334155; margin-bottom: 8px; }
            .q-body { font-size: 14px; }
            .muted { color: #64748b; font-size: 13px; }
            .line-input { margin: 8px 0; color: #0f172a; }
            .answer-box { margin-top: 8px; border: 1px solid #94a3b8; border-radius: 6px; min-height: 140px; background: #fff; }
            .field-label { margin: 10px 0 4px; font-size: 13px; font-weight: 600; color: #334155; }
            .option-list { margin: 0; padding: 0; list-style: none; }
            .option-list li { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
            .choice-box { width: 13px; height: 13px; border: 1px solid #64748b; border-radius: 3px; display: inline-block; flex-shrink: 0; }
            .suggestion-list { margin: 0; padding-left: 16px; color: #334155; }
            .suggestion-list li { margin-bottom: 4px; }
            .matrix-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
            .matrix-table th, .matrix-table td { border: 1px solid #cbd5e1; padding: 6px; font-size: 12px; text-align: left; vertical-align: middle; }
            .matrix-table th { background: #f8fafc; }
            @media print { body { margin: 16mm; } }
          </style>
        </head>
        <body>${sectionsHtml}</body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
    popup.close();
  };

  const exportPageToPdf = ({ pageLabel, title, description, pageQuestions = [] }) => {
    openPrintableDocument({
      documentTitle: title || pageLabel || 'Seite',
      sections: [{ pageLabel, title, description, pageQuestions }]
    });
  };

  const exportAllPagesToPdf = () => {
    const orderedSectionTitles = orderedEditorQuestionPages.map((item) => (
      item.type === 'default'
        ? (defaultSectionTitle || 'Formale Fragen')
        : (item.page?.title || `Seite ${item.overallOrder}`)
    ));
    if (reviewSectionTitle) orderedSectionTitles.push(reviewSectionTitle);

    const introContentHtml = `
      ${introWelcomeText ? `<p class="intro-paragraph">${toHtmlParagraph(introWelcomeText)}</p>` : ''}
      ${introOverviewText ? `<p class="intro-paragraph">${toHtmlParagraph(introOverviewText)}</p>` : ''}
      ${orderedSectionTitles.length > 0 ? `
        <div class="section-list-title">Abschnittsliste</div>
        <ul class="section-list">
          ${orderedSectionTitles.map((title) => `<li>${escapeHtml(stripHtml(title))}</li>`).join('')}
        </ul>` : ''}
      ${introAfterSectionsText ? `<p class="intro-paragraph">${toHtmlParagraph(introAfterSectionsText)}</p>` : ''}
      ${introTimerText ? `<p class="intro-paragraph">${toHtmlParagraph(introTimerText)}</p>` : ''}
      ${introStartButtonText ? `<p class="intro-paragraph"><strong>${toHtmlParagraph(introStartButtonText)}</strong></p>` : ''}
    `;

    const sections = [
      {
        pageLabel: 'Seite 1 (Einführungsseite)',
        title: 'Einführungsseite',
        customContentHtml: introContentHtml,
        showEmptyQuestionsMessage: false,
        pageQuestions: []
      },
      ...orderedEditorQuestionPages.map((item) => {
        if (item.type === 'default') {
          return {
            pageLabel: `Seite ${item.overallOrder} (Formale Fragen)`,
            title: defaultSectionTitle || 'Formale Fragen',
            description: defaultSectionDescription || '',
            pageQuestions: defaultQuestions || []
          };
        }
        return {
          pageLabel: `Seite ${item.overallOrder}`,
          title: item.page?.title || `Seite ${item.overallOrder}`,
          description: item.page?.description || '',
          pageQuestions: questions.filter((q) => q.pageId === item.page?.id)
        };
      })
    ];

    openPrintableDocument({
      documentTitle: 'Fragebogen',
      sections
    });
  };

  return (
          <div className="mb-2">
            {/* One line, always. The rail is a ~22rem column, so nothing here may
                wrap: title left, add right, everything rarer behind the menu. */}
            <div className="flex items-center gap-2 mb-4">
              <h3 className="grow min-w-0 truncate text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Fragen-Seiten
              </h3>
              <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                {questionPages.length + 2}
              </span>
              <button
                type="button"
                onClick={toggleAllQuestionLists}
                title={showAllQuestionLists ? 'Alle Fragenlisten einklappen' : 'Alle Fragen anzeigen'}
                aria-label={showAllQuestionLists ? 'Alle Fragenlisten einklappen' : 'Alle Fragen anzeigen'}
                className={`shrink-0 p-1.5 rounded-md transition-colors ${
                  showAllQuestionLists
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100'
                }`}
              >
                <HiBars3 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={addQuestionPage}
                title="Seite hinzufügen"
                aria-label="Seite hinzufügen"
                className="shrink-0 p-1.5 rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" /></svg>
              </button>
              <OverflowMenu
                className="shrink-0"
                label="Fragebogen-Aktionen"
                items={[
                  {
                    label: 'Alle Seiten drucken',
                    icon: <HiPrinter className="w-4 h-4" />,
                    onClick: exportAllPagesToPdf,
                  },
                  onExport && {
                    label: 'Fragebogen exportieren',
                    separatorBefore: true,
                    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>,
                    onClick: onExport,
                  },
                  onImport && {
                    label: 'Fragebogen importieren',
                    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
                    onClick: onImport,
                  },
                ]}
              />
            </div>
            <div className="space-y-2">
              <div className="space-y-2">
                <div
                  onClick={() => setSelectedPageId(INTRO_PAGE_ID)}
                  className={railCardClasses(isIntroPageSelected)}
                >
                  {isIntroPageSelected && <SelectionBar />}
                  <div className="flex items-center gap-3">
                    <PageNumber value={1} isSelected={isIntroPageSelected} />
                    <div className="grow min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        Einführungsseite
                      </div>
                      <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                        Willkommen, Timer, Start-Button
                      </div>
                    </div>
                  </div>
                </div>
                {orderedEditorQuestionPages.map((item) => {
                  if (item.type === 'default') {
                    const isSelected = isDefaultQuestionsPageSelected;

                    return (
                      <div
                        key={item.id}
                        onClick={(e) => handlePageCardSelect(e, DEFAULT_QUESTIONS_PAGE_ID)}
                        onDragOver={(e) => handlePageCardDragOver(e, item.id)}
                        onDragLeave={() => handlePageCardDragLeave(item.id)}
                        onDrop={(e) => handlePageCardDrop(e, item.id)}
                        className={railCardClasses(isSelected, {
                          isDragged: draggedPageCardId === item.id,
                          isDragOver: dragOverPageCardId === item.id && draggedPageCardId !== item.id,
                        })}
                      >
                        {isSelected && <SelectionBar />}
                        <div className="flex items-center gap-3">
                          <PageNumber value={item.overallOrder} isSelected={isSelected} />

                          <div className="grow min-w-0">
                            <input
                              type="text"
                              value={defaultSectionTitle}
                              onChange={(e) => setDefaultSectionTitle(e.target.value)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onDragStart={(e) => e.stopPropagation()}
                              className="w-full truncate bg-transparent border-0 p-0 text-sm font-semibold text-slate-900 dark:text-slate-100 focus:ring-0 focus:outline-hidden"
                              placeholder="Formale Fragen"
                            />
                            <MetaToggle
                              isExpanded={isQuestionListExpanded(DEFAULT_QUESTIONS_PAGE_ID)}
                              onToggle={() => toggleQuestionList(DEFAULT_QUESTIONS_PAGE_ID)}
                            >
                              {totalFormalQuestions} {totalFormalQuestions === 1 ? 'Frage' : 'Fragen'}
                            </MetaToggle>
                          </div>

                          <RailControls
                            isSelected={isSelected}
                            onDragStart={(e) => handlePageCardDragStart(e, item.id)}
                            onDragEnd={handlePageCardDragEnd}
                            onUp={() => moveDefaultQuestionsPage('up')}
                            onDown={() => moveDefaultQuestionsPage('down')}
                            disableUp={item.questionFlowOrder === 1}
                            disableDown={item.questionFlowOrder === orderedEditorQuestionPages.length}
                            menuItems={[
                              {
                                label: 'Als PDF exportieren',
                                icon: <HiPrinter className="w-4 h-4" />,
                                onClick: () => exportPageToPdf({
                                  pageLabel: `Seite ${item.overallOrder} (Formale Fragen)`,
                                  title: defaultSectionTitle || 'Formale Fragen',
                                  description: defaultSectionDescription || '',
                                  pageQuestions: defaultQuestions || []
                                }),
                              },
                            ]}
                          />
                        </div>

                        {/* Editing details only for the open page — the rail stays scannable. */}
                        {isSelected && (
                          <div className="mt-2 pl-10" onClick={(e) => e.stopPropagation()}>
                            <textarea
                              value={defaultSectionDescription}
                              onChange={(e) => setDefaultSectionDescription(e.target.value)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDragStart={(e) => e.stopPropagation()}
                              rows={1}
                              className="w-full bg-transparent border-0 p-0 text-xs text-slate-500 dark:text-slate-400 focus:ring-0 placeholder-slate-400 dark:placeholder-slate-600 resize-none"
                              placeholder="Beschreibung für Bewerber (optional)"
                              style={{ minHeight: '1.25rem' }}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                            />
                          </div>
                        )}

                        {isQuestionListExpanded(DEFAULT_QUESTIONS_PAGE_ID) && (
                          <div className="mt-2 pl-10" onClick={(e) => e.stopPropagation()}>
                            {renderQuestionOverviewList({
                              pageKey: DEFAULT_QUESTIONS_PAGE_ID,
                              pageQuestions: defaultQuestions || [],
                              onReorder: onReorderDefaultQuestions,
                              onMove: onMoveDefaultQuestion
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const page = item.page;
                  const pageQuestions = questions.filter((q) => q.pageId === page.id);
                  const questionCount = pageQuestions.length;
                  const isSelected = page.id === selectedPageId;

                  return (
                    <div
                      key={page.id}
                      onClick={(e) => handlePageCardSelect(e, page.id)}
                      onDragOver={(e) => handlePageCardDragOver(e, page.id)}
                      onDragLeave={() => handlePageCardDragLeave(page.id)}
                      onDrop={(e) => handlePageCardDrop(e, page.id)}
                      className={railCardClasses(isSelected, {
                        isDragged: draggedPageCardId === page.id,
                        isDragOver: dragOverPageCardId === page.id && draggedPageCardId !== page.id,
                      })}
                    >
                      {isSelected && <SelectionBar />}

                      <div className="flex items-center gap-3">
                        <PageNumber value={item.overallOrder} isSelected={isSelected} />

                        <div className="grow min-w-0">
                          <input
                            type="text"
                            value={page.title}
                            onChange={(e) => renameQuestionPage(page.id, e.target.value)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.stopPropagation()}
                            className="w-full truncate bg-transparent border-0 p-0 text-sm font-semibold text-slate-900 dark:text-slate-100 focus:ring-0 focus:outline-hidden"
                            placeholder={`Titel für Seite ${item.overallOrder}`}
                          />
                          <MetaToggle
                            isExpanded={isQuestionListExpanded(page.id)}
                            onToggle={() => toggleQuestionList(page.id)}
                          >
                            {normalizeCustomPageType(page.pageType, PAGE_TYPE_FACH) === PAGE_TYPE_BASIS
                              ? 'Basisfragen'
                              : 'Fachspezifisch'}
                            {' · '}
                            {questionCount} {questionCount === 1 ? 'Frage' : 'Fragen'}
                          </MetaToggle>
                        </div>

                        <RailControls
                          isSelected={isSelected}
                          onDragStart={(e) => handlePageCardDragStart(e, page.id)}
                          onDragEnd={handlePageCardDragEnd}
                          onUp={() => moveQuestionPage(page.id, 'up')}
                          onDown={() => moveQuestionPage(page.id, 'down')}
                          disableUp={item.questionFlowOrder === 1}
                          disableDown={item.questionFlowOrder === orderedEditorQuestionPages.length}
                          menuItems={[
                            {
                              label: 'Als PDF exportieren',
                              icon: <HiPrinter className="w-4 h-4" />,
                              onClick: () => exportPageToPdf({
                                pageLabel: `Seite ${item.overallOrder}`,
                                title: page.title || `Seite ${item.overallOrder}`,
                                description: page.description || '',
                                pageQuestions: questions.filter((q) => q.pageId === page.id)
                              }),
                            },
                            {
                              label: 'Seite löschen',
                              separatorBefore: true,
                              danger: true,
                              icon: <HiTrash className="w-4 h-4" />,
                              onClick: () => removeQuestionPage(page.id),
                            },
                          ]}
                        />
                      </div>

                      {/* Editing details only for the open page — the rail stays scannable. */}
                      {isSelected && (
                        <div className="mt-2 pl-10 space-y-2" onClick={(e) => e.stopPropagation()}>
                          <textarea
                            value={page.description || ''}
                            onChange={(e) => updateQuestionPageDescription(page.id, e.target.value)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.stopPropagation()}
                            rows={1}
                            className="w-full bg-transparent border-0 p-0 text-xs text-slate-500 dark:text-slate-400 focus:ring-0 placeholder-slate-400 dark:placeholder-slate-600 resize-none"
                            placeholder="Beschreibung für Bewerber (optional)"
                            style={{ minHeight: '1.25rem' }}
                            onInput={(e) => {
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                          />
                          <select
                            value={normalizeCustomPageType(page.pageType, PAGE_TYPE_FACH)}
                            onChange={(e) => updateQuestionPageType(page.id, e.target.value)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.stopPropagation()}
                            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-200"
                            title="Seitentyp"
                          >
                            <option value={PAGE_TYPE_BASIS}>Basisfragen</option>
                            <option value={PAGE_TYPE_FACH}>Fachspezifische Fragen</option>
                          </select>
                        </div>
                      )}

                      {isQuestionListExpanded(page.id) && (
                        <div className="mt-2 pl-10" onClick={(e) => e.stopPropagation()}>
                          {renderQuestionOverviewList({
                            pageKey: page.id,
                            pageQuestions,
                            onReorder: (fromIndex, toIndex) => onReorderPageQuestions?.(page.id, fromIndex, toIndex),
                            onMove: (index, direction) => onMovePageQuestion?.(page.id, index, direction)
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
  );
}
